import { BreachCalculationType, UserRes, JobsiteRes, VehicleRes, ExtCompanyRes, ShiftCommentRes, PreCheckAnswerRes, EnumRes, ShiftWarningRes, ShiftRes, ShiftFleetIoFormSubmissionRes } from '../proxy/proxy';
import { getFullName, getDateDifferenceInMS, getBreakBreach } from '../utils/common';
import FormattedShiftNR from './FormattedShiftNR';

const checkInType30Min = 64;
const checkInTypePreCheck = 128;
class FormattedShift {
  id: number;
  shiftStatusId: number;
  shiftStatus: string;
  driverArrivalTimeLocal: string;
  scheduledStartUTC: string;
  scheduledStartActualUTC: string;
  scheduledEndUTC: string;
  startedOnUTC: string;
  startedOnActualUTC: string;
  endedOnUTC: string;
  endedOnActualUTC: string;
  scheduledWeight: number;
  scheduledKms: number;
  scheduledDrops: number;
  scheduledDifficulty: number;
  scheduledTotalWorkHours: number;
  instructions: string;
  breachCalculatedUsing: BreachCalculationType;
  routeId: string;
  driverComments: string = '';
  user: UserRes;
  userName: string;
  userEmail: string;
  userPhone: string;
  userExtDriverId: string;
  userHasAccepted: boolean;
  jobsite: JobsiteRes;
  jobsiteName: string;
  vehicle: VehicleRes;
  vehicleRego: string;
  shiftSupervisorName: string = '--';
  shiftSupervisorPhone: string = '--';
  unlistedVehicleRego: string;
  extCompany: ExtCompanyRes;
  startOdometerKms: number;
  endOdometerKms: number;
  dock: string;
  amOrPm: string;
  jotFormUrl: string;
  shiftBreaks: Array<{
    id: number,
    startedOnUTC: string,
    endedOnUTC: string,
    breakTime: number
  }>;
  shiftComments: Array<ShiftCommentRes>;
  preCheckAnswers: Array<PreCheckAnswerRes>;
  totalShiftTime: number | string;
  totalBreakTime: number | string;
  totalTimeWorked: number | string;
  breach: any;
  checkins: Array<any>;
  checkinStatus: string;
  checkinPhoto: string;
  shiftNRs: Array<any>;
  timesheetStatus: string;
  isNew: boolean = false;
  shiftWarnings: ShiftWarningRes[] = [];

  // Route performance metrics
  routePerformanceWeekEnding: string | null;
  routePerformanceOnTimeOrders: number | null;
  routePerformanceOrders: number | null;
  routePerformancePlannedReload: boolean | null;
  routePerformanceOtdPercentage: number | null;
  routePerformanceOveragePercentage: number | null;
  routePerformanceLateArrivalMinsPerRoute: number | null;
  routePerformanceDwellPerRoute: number | null;
  routePerformanceUnloadingPerRoute: number | null;
  routePerformanceWGOIdlePerRoute: number | null;
  routePerformanceMicroliseCustomerIdlePerRoute: number | null;
  routePerformanceMicroliseEnrouteIdlePerRoute: number | null;
  routePerformanceMicroliseIgnitionOffOutsideGeofence: number | null;
  routePerformanceTotalIdleTimePerRoute: number | null;
  routePerformanceCheckoutOutsideGeofencePerRoute: number | null;
  routePerformanceServiceAndUnknownOveragePerRoute: number | null;
  routePerformanceDriveAndUnknownOveragePerRoute: number | null;

  // Truck Return Details
  estimatedVehicleReturnLocal: string | null;
  estimatedVehicleReturnUtcRequestedByUser: string | null;
  nextShiftVehicleDueByLocal: string | null;
  previousShiftDriverPhone: string | null;
  nextShiftDriverName: string | null;

  // OTD metrics
  otdTotalLateWindows: number | null;
  otdPercentage: number | null;
  otdLateArrivalMinutes: number | null;
  otdLateDepartureMinutes: number | null;

  shiftFleetIoFormSubmissions: ShiftFleetIoFormSubmissionRes[] | null;

  shiftVoiceOfCustomerComments: Array<{
    id: number;
    orderNumber: string;
    routeId: string;
    deliveryComment: string;
    careComment: string;
    friendliness: string;
    presentation: string;
    correctiveFormUrl?: string;
    deliveryDate: string;
    deliveryWindow: string;
    createdOnUtc: string;
    updatedOnUtc: string;
    shiftId: number;
  }>;

  constructor(rawData: any = null, shiftStatusTypes: Array<EnumRes> = []) {
    this.id = rawData?.id;
    this.shiftStatusId = rawData?.shiftStatus;
    this.shiftStatus = shiftStatusTypes?.find((type) => type.id === rawData?.shiftStatus)?.value ?? '';

    this.driverArrivalTimeLocal = rawData?.driverArrivalTimeLocal || '';
    this.scheduledStartUTC = rawData?.scheduledStartLocal || '';
    this.scheduledStartActualUTC = rawData?.scheduledStartUTC || '';
    this.scheduledEndUTC = rawData?.scheduledEndLocal || '';
    this.startedOnUTC = rawData?.startedOnLocal || '';
    this.startedOnActualUTC = rawData?.startedOnUTC || '';
    this.endedOnUTC = rawData?.endedOnLocal || '';
    this.endedOnActualUTC = rawData?.endedOnUTC || '';

    this.scheduledWeight = rawData?.scheduledWeight;
    this.scheduledKms = rawData?.scheduledKms;
    this.scheduledDrops = rawData?.scheduledDrops;
    this.scheduledDifficulty = rawData?.scheduledDifficulty;
    this.scheduledTotalWorkHours = rawData?.scheduledTotalWorkHours;

    this.instructions = rawData?.instructions;

    this.breachCalculatedUsing = rawData?.breachCalculatedUsing;

    this.routeId = rawData?.routeId;
    this.driverComments = rawData?.driverComments;

    this.user = rawData?.user;
    this.userName = getFullName(rawData?.user?.firstName, rawData?.user?.middleName, rawData?.user?.lastName);
    this.userEmail = rawData?.user?.email;
    this.userPhone = rawData?.user?.phoneNumber;
    this.userExtDriverId = rawData?.user?.extDriverId;
    this.userHasAccepted = rawData?.userHasAccepted;

    this.jobsite = rawData?.jobsite;
    this.jobsiteName = rawData?.jobsite?.name;

    this.vehicle = rawData?.vehicle;
    this.vehicleRego = rawData?.vehicle?.registrationNumber;
    this.unlistedVehicleRego = rawData?.unlistedVehicleRego;

    this.extCompany = rawData?.extCompany;

    this.startOdometerKms = rawData?.startOdometerKms;
    this.endOdometerKms = rawData?.endOdometerKms;
    this.jotFormUrl = rawData?.jotFormUrl;
    this.shiftWarnings = rawData?.shiftWarnings;

    this.routePerformanceWeekEnding = rawData?.routePerformanceWeekEnding;
    this.routePerformanceOnTimeOrders = rawData?.routePerformanceOnTimeOrders;
    this.routePerformanceOrders = rawData?.routePerformanceOrders;
    this.routePerformancePlannedReload = rawData?.routePerformancePlannedReload;
    this.routePerformanceOtdPercentage = rawData?.routePerformanceOtdPercentage;
    this.routePerformanceOveragePercentage = rawData?.routePerformanceOveragePercentage;
    this.routePerformanceLateArrivalMinsPerRoute = rawData?.routePerformanceLateArrivalMinsPerRoute;
    this.routePerformanceDwellPerRoute = rawData?.routePerformanceDwellPerRoute;
    this.routePerformanceUnloadingPerRoute = rawData?.routePerformanceUnloadingPerRoute;
    this.routePerformanceWGOIdlePerRoute = rawData?.routePerformanceWGOIdlePerRoute;
    this.routePerformanceMicroliseCustomerIdlePerRoute = rawData?.routePerformanceMicroliseCustomerIdlePerRoute;
    this.routePerformanceMicroliseEnrouteIdlePerRoute = rawData?.routePerformanceMicroliseEnrouteIdlePerRoute;
    this.routePerformanceMicroliseIgnitionOffOutsideGeofence = rawData?.routePerformanceMicroliseIgnitionOffOutsideGeofence;
    this.routePerformanceTotalIdleTimePerRoute = rawData?.routePerformanceTotalIdleTimePerRoute;
    this.routePerformanceCheckoutOutsideGeofencePerRoute = rawData?.routePerformanceCheckoutOutsideGeofencePerRoute;
    this.routePerformanceServiceAndUnknownOveragePerRoute = rawData?.routePerformanceServiceAndUnknownOveragePerRoute;
    this.routePerformanceDriveAndUnknownOveragePerRoute = rawData?.routePerformanceDriveAndUnknownOveragePerRoute;

    this.estimatedVehicleReturnLocal = rawData?.estimatedVehicleReturnLocal;
    this.estimatedVehicleReturnUtcRequestedByUser = rawData?.estimatedVehicleReturnUtcRequestedByUser;
    this.nextShiftVehicleDueByLocal = rawData?.nextShiftVehicleDueByLocal;
    this.previousShiftDriverPhone = rawData?.previousShiftDriverPhone;
    this.nextShiftDriverName = rawData?.nextShiftDriverName;

    this.otdTotalLateWindows = rawData?.otdTotalLateWindows;
    this.otdPercentage = rawData?.otdPercentage;
    this.otdLateArrivalMinutes = rawData?.otdLateArrivalMinutes;
    this.otdLateDepartureMinutes = rawData?.otdLateDepartureMinutes;

    this.shiftFleetIoFormSubmissions = rawData?.shiftFleetIoFormSubmissions;

    this.shiftVoiceOfCustomerComments = rawData?.shiftVoiceOfCustomerComments || [];

    this.shiftBreaks = (rawData?.shiftBreaks || []).map((brk: any) => {
      const breakTime = getDateDifferenceInMS(brk.startedOnUTC, brk.endedOnUTC || new Date().toISOString());
      return {
        id: brk.id,
        startedOnUTC: brk.startedOnLocal,
        startedOnActualUTC: brk.startedOnUTC,
        endedOnUTC: brk.endedOnLocal,
        breakTime
      };
    });

    // TO BE VERIFIED
    this.shiftComments = rawData?.shiftComments;
    this.preCheckAnswers = rawData?.preCheckAnswers;
    this.checkins = rawData?.checkins;
    this.checkinStatus = this.getCheckinStatus();
    this.checkinPhoto = this.getCheckinPhoto();
    this.shiftNRs = rawData?.shiftNRs?.map((shift: any) => new FormattedShiftNR(shift));
    this.timesheetStatus = this.getTimesheetStatus();

    this.totalShiftTime = rawData?.endedOnUTC ? getDateDifferenceInMS(rawData?.startedOnUTC, rawData?.endedOnUTC, true) : '';
    this.totalBreakTime = this.shiftBreaks.reduce((a, b) => a + b.breakTime, 0) || '';
    this.totalTimeWorked = rawData?.endedOnUTC ? Number(this.totalShiftTime) - Number(this.totalBreakTime) : '';
    this.dock = rawData?.dock ?? '';
    this.amOrPm = rawData?.amOrPm ?? '';

    this.breach = !rawData?.endedOnUTC ? getBreakBreach(this) : null;
    this.isNew = false;
  }

  getTimesheetStatus() {
    if (this.shiftNRs?.length === 0) return 'No';
    if (this.shiftNRs?.length === 1) return 'Yes';
    return 'Duplicate';
  }
  getCheckinStatus() {
    if (this.checkins?.length > 0) return 'Yes';
    return 'No';
  }
  getCheckinPhoto() {
    if (this.checkins?.length > 0) {
      return this.checkins?.find((checkin) => checkin.checkinType === checkInType30Min)?.photoSasUrl ?? '';
    }

    return '';
  }
}

export default FormattedShift;
